// Turns API day grids into timeline columns (SPEC §6.4, §9.3).

import type {
  ClassroomColumn, DayGrid, GridProblem, Lesson, MyUnavailableBlock, UnavailableBlock,
} from "../api/types";
import { t, translateCode } from "../i18n/i18n";
import { formatRange } from "../time";
import { describeLessonFlags, lessonRoute, lessonStateClass } from "./lessonLabels";
import type { TimelineBlock, TimelineColumn, TimelineLesson } from "./timeline";

/** One timeline column for a day grid. Leave the room out of the blocks when the column already is a room. */
export function gridToColumn(title: string, titleRoute: string | null, grid: DayGrid, showRoom = true):
  TimelineColumn {
  const problemKeys: Set<string> = collectProblemKeys(grid.problems);
  return {
    title,
    titleRoute,
    lessons: grid.lessons.map((lesson: Lesson): TimelineLesson => toTimelineLesson(lesson, problemKeys, showRoom)),
    unavailable: grid.unavailable.map(toTimelineBlock),
  };
}

/** A classroom's column in a day view, optionally under its center's name (all-centers grid, SPEC §7.2). */
export function classroomToColumn(column: ClassroomColumn, centerName: string | undefined): TimelineColumn {
  const timelineColumn: TimelineColumn = gridToColumn(column.classroomName, `/admin/classrooms/${column.classroomId}`,
    column.grid, false);
  if (centerName !== undefined) {
    timelineColumn.groupTitle = centerName;
  }

  return timelineColumn;
}

/** A lesson block: group, time, teacher (and room), and text labels for its state. */
export function toTimelineLesson(lesson: Lesson, problemKeys: Set<string>, showRoom = true): TimelineLesson {
  const hasProblem: boolean = problemKeys.has(keyText(lesson.key));
  const labels: string[] = describeLessonFlags(lesson);
  if (hasProblem && labels.includes(t("lessonFlags.problem")) === false) {
    labels.push(t("lessonFlags.problem"));
  }

  return {
    range: lesson.range,
    title: lesson.groupName,
    lines: showRoom ? [formatRange(lesson.range), lesson.teacherName, lesson.classroomName]
      : [formatRange(lesson.range), lesson.teacherName],
    labels,
    stateClass: lessonStateClass(lesson, hasProblem),
    route: lessonRoute(lesson),
  };
}

/** Hatched unavailable time with its reason (and the admin's note, when the block has one). */
export function toTimelineBlock(block: UnavailableBlock | MyUnavailableBlock): TimelineBlock {
  const reason: string = translateCode("unavailabilityReasons", block.reason);
  const note: string | null = "note" in block ? block.note : null;
  const label: string = note === null ? reason : `${reason}: ${note}`;
  return { range: block.range, label };
}

/** Lesson keys that have a grid problem. */
export function collectProblemKeys(problems: GridProblem[]): Set<string> {
  const keys: Set<string> = new Set<string>();
  for (const problem of problems) {
    keys.add(keyText(problem.lesson));
    if (problem.otherLesson !== null) {
      keys.add(keyText(problem.otherLesson));
    }
  }

  return keys;
}

function keyText(key: { groupId: string; date: string; slotId: string }): string {
  return `${key.groupId}/${key.date}/${key.slotId}`;
}
